import * as React from "react";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { PercentageTextBoxEditor } from "../../../../../../../react-core/Html/PercentageTextBoxEditor";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { BankAccountIdType } from "../../../../../../../Wrap/Constants/Enums/Domain/BankAccountIdType";
import { IncomePaymentDestination } from "../../../../../../../Wrap/Constants/Enums/Domain/IncomePaymentDestination";
import { BankAccount } from "../../../../../../../Wrap/Models/Domains/BankAccount/BankAccount";
import { DrawdownPaymentDestinationAllocation } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownPaymentDestinationAllocation";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { BankDetailsContent } from "../../Resource/Content";
import { CircleBtn, CircleBtnType } from "../../../../../../../react-core/Html/CircleBtn";
import { Accordion } from "../../../../../../../react-core/Html/Accordion";
import { NewBankAccountInstruction } from "../../../Common/PaymentsIn/BankAccount/NewBankAccountInstruction";
import { PaymentType } from "../../../Common/Resource/Contents";
import { BankAccountOwner } from "../../../../../../../Wrap/Constants/Enums/Domain/BankAccountOwner";
import { BankAccountAction } from "../../../Common/PaymentsIn/BankAccount/BankAccountAction";
import { BankAccountInstruction } from "../../../../../../../Wrap/Models/Domains/Instructions/BankAccountInstruction";
import { InstructionStatus } from "../../../../../../../Wrap/Constants/Enums/Domain/InstructionStatus";
import { CountryCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CountryCode";
import { PaymentMethod } from "../../../../../../../Wrap/Constants/Enums/Domain/PaymentMethod";

interface IBankDetailsProps extends IProcessComponentProps {
    ownerHierarchyId: string;
    allBankAccounts: BankAccount[];
    incomePaymentAllocations: DrawdownPaymentDestinationAllocation[];
    onChange: (newAllocations: DrawdownPaymentDestinationAllocation[]) => void;

    bankAccountInstructions?: BankAccountInstruction[];
    onChangeBankAccountInstructions?: (changes: BankAccountInstruction[]) => void;
}
interface IBankDetailsState {
    splitAcrossTwo: boolean;
    addNewBankAccount: boolean;
}

type BankAccountDetails = {
    type: BankAccountIdType;
    id: number;
    accountNumber: number;
    destination: IncomePaymentDestination;
    displayString: string;
    incomeAllocation?: number;
    bankCountry?: CountryCode;
};

// Bank account number can only be 8 digits so nine nines will always be unique and last
const dummyWrapCashBankAccountNumber = 999999999;

export class BankDetails extends ProcessComponent<IBankDetailsProps, IBankDetailsState> {
    constructor(props: any) {
        super(props);
        this.state = { splitAcrossTwo: false, addNewBankAccount: false };
    }

    isValid() {
        if (this.props.incomePaymentAllocations?.length == 0) {
            return false;
        }
        return true;
    }

    componentDidMount() {
        super.componentDidMount();
        const { incomePaymentAllocations } = this.props;
        if (incomePaymentAllocations.length > 1) {
            this.setState({ splitAcrossTwo: true });
        }
    }

    private handleBankAccountChange(bankAccountDetails: BankAccountDetails, bankAccountIndex: number) {
        const { incomePaymentAllocations, onChange } = this.props;

        if (bankAccountDetails === null || bankAccountDetails.destination === null) {
            incomePaymentAllocations.pop();
        } else {
            if (incomePaymentAllocations.length > bankAccountIndex) {
                incomePaymentAllocations[bankAccountIndex] = {
                    BankAccountIdType: bankAccountDetails.type,
                    BankAccountId: bankAccountDetails.id,
                    Destination: bankAccountDetails.destination,
                    Allocation: bankAccountDetails.incomeAllocation,
                    Method: bankAccountDetails.bankCountry && bankAccountDetails.bankCountry != CountryCode.GBR ? PaymentMethod.Swift : PaymentMethod.NotSpecified
                };
            } else {
                incomePaymentAllocations.push({
                    BankAccountIdType: bankAccountDetails.type,
                    BankAccountId: bankAccountDetails.id,
                    Destination: bankAccountDetails.destination,
                    Allocation: bankAccountDetails.incomeAllocation,
                    Method: bankAccountDetails.bankCountry && bankAccountDetails.bankCountry != CountryCode.GBR ? PaymentMethod.Swift : PaymentMethod.NotSpecified
                });
            }
        }

        onChange(incomePaymentAllocations);
    }

    private saveNewBankAccountInstruction(bankAccountInstruction: BankAccountInstruction) {
        const { onChangeBankAccountInstructions, bankAccountInstructions, ownerHierarchyId } = this.props;

        if (
            (bankAccountInstructions || []).findIndex((x) => x.AccountNumber == bankAccountInstruction.AccountNumber) ==
            -1
        ) {
            bankAccountInstruction.OwnerHierarchyId = ownerHierarchyId;
            BankAccountAction.saveBankInstruction(bankAccountInstruction, null, null, (savedBankAccountInstruction) => {
                const BankAccountInstructions = (bankAccountInstructions || []).concat(savedBankAccountInstruction);
                onChangeBankAccountInstructions(BankAccountInstructions);
                this.setState({ addNewBankAccount: false });
            });
        } else {
            return this.props.showErrorMsgPopUp(["Please enter a non-existing bank account"], true);
        }
    }

    public render(): JSX.Element {
        const { allBankAccounts, bankAccountInstructions, incomePaymentAllocations, onChangeBankAccountInstructions } = this.props;
        const { addNewBankAccount } = this.state;

        const bankAccounts: Array<BankAccountDetails> = [
            {
                type: null,
                id: null,
                accountNumber: null,
                destination: null,
                displayString: "Please select",
                bankCountry: CountryCode.NotSpecified
            },
        ];

        allBankAccounts?.forEach((account) => {
            if (account.NominatedForWithdrawal && (account.Owner == BankAccountOwner.Individual || account.Owner == BankAccountOwner.Joint)) {
                bankAccounts.push({
                    type: BankAccountIdType.BankAccount,
                    id: account.BankAccountId,
                    accountNumber: Number(account.AccountNumber),
                    destination: IncomePaymentDestination.BankAccount,
                    displayString: `${account.SortCode} | ${account.AccountNumber} | ${account.AccountName}`,
                    bankCountry: account.BankCountry
                });
            }
        });

        bankAccountInstructions?.filter(account => account.Status == InstructionStatus.Pending).forEach((account) => {
            if (account.NominatedForWithdrawal && (account.Owner == BankAccountOwner.Individual || account.Owner == BankAccountOwner.Joint)) {
                const sortCode = `${account.BankCodes?.BankCode}${account.BankCodes?.BranchCode}${account.BankCodes?.ExtraCode}`
                bankAccounts.push({
                    type: BankAccountIdType.BankAccountInstruction,
                    id: account.Id,
                    accountNumber: Number(account.AccountNumber),
                    destination: IncomePaymentDestination.BankAccount,
                    displayString: `${sortCode} | ${account.AccountNumber} | ${account.AccountName}`,
                });
            }
        });

        bankAccounts.push({
            type: null,
            id: null,
            accountNumber: dummyWrapCashBankAccountNumber,
            destination: IncomePaymentDestination.WrapCash,
            displayString: "Cash Account",
        });

        const bankAccountOptions = bankAccounts.map((account) => account.accountNumber);

        let bankAccount1Details: BankAccountDetails;
        if (incomePaymentAllocations.length > 0) {
            bankAccount1Details = bankAccounts.find((details) => {
                if (details.destination === IncomePaymentDestination.WrapCash) {
                    return incomePaymentAllocations[0].Destination === IncomePaymentDestination.WrapCash;
                } else {
                    return (
                        incomePaymentAllocations[0].Destination === IncomePaymentDestination.BankAccount &&
                        details.type === incomePaymentAllocations[0].BankAccountIdType &&
                        details.id === incomePaymentAllocations[0].BankAccountId
                    );
                }
            });
            if (bankAccount1Details && incomePaymentAllocations?.[0].Allocation != undefined){
                bankAccount1Details.incomeAllocation = incomePaymentAllocations?.[0].Allocation;
            }
        }

        let bankAccount2Details: BankAccountDetails;
        if (incomePaymentAllocations.length > 1) {
            bankAccount2Details = bankAccounts.find((details) => {
                if (details.destination === IncomePaymentDestination.WrapCash) {
                    return incomePaymentAllocations[1].Destination === IncomePaymentDestination.WrapCash;
                } else {
                    return (
                        incomePaymentAllocations[1].Destination === IncomePaymentDestination.BankAccount &&
                        details.type === incomePaymentAllocations[1].BankAccountIdType &&
                        details.id === incomePaymentAllocations[1].BankAccountId
                    );
                }
            });
            bankAccount2Details.incomeAllocation = incomePaymentAllocations[1].Allocation;
        }
        const showAddBank = onChangeBankAccountInstructions && !addNewBankAccount;
        return (
            <>
                <div className="h4 mb-10" data-test="">
                    {BankDetailsContent.labels.selectBankAccount}
                </div>
                {/* 2 - one please select, one bank account */}
                {bankAccounts.length > 2 && (
                    <div className="row">
                        <div className="col-12">
                            <CheckboxEditor
                                label={BankDetailsContent.labels.splitAcrossTwoBankAccounts}
                                value={this.state.splitAcrossTwo}
                                onChange={(v) => {
                                    !v && this.handleBankAccountChange(null, 1);
                                    if(v){
                                        this.handleBankAccountChange(bankAccounts[1], 0);
                                        this.handleBankAccountChange(bankAccounts[2], 1);
                                    }
                                    this.setState({ splitAcrossTwo: v });
                                }}
                                cssWrapper="square-checkbox"
                            />
                        </div>
                    </div>
                )}
                <div className="row">
                    <div className="col-4">
                        <SelectEditor
                            label={this.state.splitAcrossTwo ? BankDetailsContent.labels.bankAccount1 : ""}
                            options={bankAccountOptions}
                            selected={bankAccount1Details && bankAccount1Details.accountNumber}
                            getText={(v) => {
                                return bankAccounts.find((option) => option.accountNumber === v).displayString;
                            }}
                            onChange={(v) => {
                                const bankAccountDetails = bankAccounts.find((account) => account.accountNumber === v);
                                this.handleBankAccountChange(bankAccountDetails, 0);
                            }}
                            required={"Please select a bank account"}
                            alwaysValidateOnUpdate
                        />
                    </div>
                </div>
                {showAddBank && (
                    <div className="row">
                        <div className={`col-6 mb-20`}>
                            <CircleBtn
                                type={CircleBtnType.add}
                                title="Add new bank account"
                                onClick={() => {
                                    this.setState({ addNewBankAccount: true });
                                }}
                            />
                        </div>
                    </div>
                )}
                {addNewBankAccount && (
                    <Accordion
                        key={`New bank account`}
                        label={`New bank account`}
                        cssWrapper="accordion-simple mb-20"
                        isOpen={true}
                    >
                        <NewBankAccountInstruction
                            onChange={(instruction) => {
                                this.saveNewBankAccountInstruction(instruction);
                            }}
                            data={{
                                NominatedForWithdrawal: true,
                                Owner: BankAccountOwner.Individual,
                            }}
                            onRef={(ref) => this.appendChild(ref)}
                            payeeType={PaymentType.ClientIncome}
                            isReview={false}
                            withdrawalsOnly
                        />
                        <CircleBtn
                            cssWrapper="mt-20"
                            type={CircleBtnType.remove}
                            title="Remove this bank account"
                            onClick={() => this.setState({ addNewBankAccount: false })}
                        />
                    </Accordion>
                )}
                {this.state.splitAcrossTwo && (
                    <>
                        <div className="row">
                            <div className="col-4">
                                <PercentageTextBoxEditor
                                    label={BankDetailsContent.labels.proportionOfIncome}
                                    value={bankAccount1Details && bankAccount1Details.incomeAllocation}
                                    atMost={{
                                        errorMessage: BankDetailsContent.messages.percentageOver100,
                                        value: 100,
                                    }}
                                    atLeast={{
                                        errorMessage: BankDetailsContent.messages.percentageUnder0,
                                        value: 0,
                                    }}
                                    onChange={(v) => {
                                        bankAccount1Details.incomeAllocation = v;
                                        this.handleBankAccountChange(bankAccount1Details, 0);
                                    }}
                                    required={"Please enter a percentage"}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-4">
                                <SelectEditor
                                    label={BankDetailsContent.labels.bankAccount2}
                                    options={
                                        bankAccount1Details
                                            ? bankAccountOptions.filter((accountNumber) => {
                                                  return (
                                                      bankAccount1Details &&
                                                      accountNumber !== bankAccount1Details.accountNumber
                                                  );
                                              })
                                            : bankAccountOptions
                                    }
                                    selected={bankAccount2Details && bankAccount2Details.accountNumber}
                                    getText={(v) => {
                                        return bankAccounts.find((option) => option.accountNumber === v).displayString;
                                    }}
                                    onChange={(v) => {
                                        const bankAccountDetails = bankAccounts.find(
                                            (account) => account.accountNumber === v
                                        );
                                        this.handleBankAccountChange(bankAccountDetails, 1);
                                    }}
                                    required={"Please select a bank account"}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-4">
                                <PercentageTextBoxEditor
                                    label={BankDetailsContent.labels.proportionOfIncome}
                                    value={bankAccount2Details && bankAccount2Details.incomeAllocation}
                                    atMost={{
                                        errorMessage: BankDetailsContent.messages.percentageOver100,
                                        value: 100,
                                    }}
                                    atLeast={{
                                        errorMessage: BankDetailsContent.messages.percentageUnder0,
                                        value: 0,
                                    }}
                                    onChange={(v) => {
                                        bankAccount2Details.incomeAllocation = v;
                                        this.handleBankAccountChange(bankAccount2Details, 1);
                                    }}
                                    validationRules={[
                                        {
                                            errorMessage: "",
                                            validator: (v) => {
                                                return bankAccount1Details?.incomeAllocation
                                                    ? v + bankAccount1Details?.incomeAllocation === 100
                                                    : false;
                                            },
                                        },
                                    ]}
                                />
                                {bankAccount1Details?.incomeAllocation + bankAccount2Details?.incomeAllocation !==
                                    100 && (
                                    <Alert
                                        message={BankDetailsContent.messages.totalProportionOfIncomeNot100}
                                        alertType={AlertType.Error}
                                    />
                                )}
                            </div>
                        </div>
                    </>
                )}
            </>
        );
    }
}

export default BankDetails;
